import { useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import type { NeighborhoodAnchor, NeighborhoodPoint } from '@avisoma/shared';
import { OWN_COLOR, neighborhoodColor } from './neighborhoodColor';

ChartJS.register(LinearScale, PointElement, Tooltip);

const RING: Record<'misplaced' | 'borderline', string> = { misplaced: '#ef4444', borderline: '#f59e0b' };

export function CategoryNeighborhoodScatter({
  points,
  anchors,
  hoveredId,
  onHover,
  onSelectCluster,
  onSelectCategory,
}: {
  points: NeighborhoodPoint[];
  anchors: NeighborhoodAnchor[];
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  onSelectCluster: (clusterId: number) => void;
  onSelectCategory: (categoryId: number) => void;
}) {
  const data = useMemo<ChartData<'scatter'>>(() => {
    const maxPageCount = points.reduce((max, point) => Math.max(max, point.pageCount), 1);
    return {
      datasets: [
        {
          label: 'clusters',
          data: points.map((point) => ({ x: point.x, y: point.y })),
          pointRadius: points.map((point) => {
            const base = 3 + 9 * Math.sqrt(point.pageCount / maxPageCount);
            return hoveredId === point.id ? base + 2 : base;
          }),
          pointHitRadius: 6,
          pointBackgroundColor: points.map(() => OWN_COLOR),
          pointBorderColor: points.map(
            (point) => RING[point.status as 'misplaced' | 'borderline'] ?? 'rgba(0,0,0,0)',
          ),
          pointBorderWidth: points.map((point) => (point.status === 'normal' ? 0 : 2.5)),
        },
        {
          label: 'anchors',
          data: anchors.map((anchor) => ({ x: anchor.x, y: anchor.y })),
          pointStyle: 'rectRot',
          pointRadius: 7,
          pointHoverRadius: 8,
          pointHitRadius: 6,
          pointBackgroundColor: anchors.map((_, index) => neighborhoodColor(index)),
          pointBorderWidth: 0,
        },
      ],
    };
  }, [points, anchors, hoveredId]);

  const options = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'nearest', intersect: true },
      onHover: (event, elements) => {
        const target = event.native?.target as HTMLElement | undefined;
        if (target) target.style.cursor = elements.length ? 'pointer' : 'default';
        const hit = elements[0];
        if (hit && hit.datasetIndex === 0) onHover(points[hit.index].id);
      },
      onClick: (_event, elements) => {
        const hit = elements[0];
        if (!hit) return;
        if (hit.datasetIndex === 0) onSelectCluster(points[hit.index].id);
        else onSelectCategory(anchors[hit.index].categoryId);
      },
      scales: { x: { display: false }, y: { display: false } },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (ctx) => {
              if (ctx.datasetIndex === 1) {
                const anchor = anchors[ctx.dataIndex];
                return [anchor.categoryName, `nearest cluster ${anchor.distance.toFixed(3)}`];
              }
              const point = points[ctx.dataIndex];
              const lines = [`#${point.id} · ${point.pageCount} pages`];
              if (point.status !== 'normal' && point.suggestedCategoryName) {
                lines.push(
                  `${point.status === 'misplaced' ? 'Closer to' : 'Near'} ${point.suggestedCategoryName} (margin ${point.margin.toFixed(3)})`,
                );
              }
              return lines;
            },
          },
        },
      },
    }),
    [points, anchors, onHover, onSelectCluster, onSelectCategory],
  );

  return (
    <div className="h-80 w-full" onMouseLeave={() => onHover(null)}>
      <Scatter data={data} options={options} />
    </div>
  );
}
